import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import MovieCard from "../components/MovieCard";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { GENRES, GENRE_DESCRIPTIONS, CONTENT_TYPES, TAGS } from "../lib/constants";

export default function Browse() {
  const [search, setSearch] = useSearchParams();
  const { user } = useAuth();
  const [movies, setMovies] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = search.get("q") || "";
  const genre = search.get("genre") || "";
  const tag = search.get("tag") || "";
  const year = search.get("year") || "";
  const is4k = search.get("is_4k") || "";
  const type = search.get("type") || "";

  const update = (key, value) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearch(next);
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (genre) params.genre = genre;
    if (tag) params.tag = tag;
    if (year) params.year = parseInt(year, 10);
    if (is4k === "true") params.is_4k = true;
    if (type) params.content_type = type;
    try {
      const { data } = await api.get("/movies", { params });
      setMovies(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadMeta = async () => {
    if (user && user !== false) {
      try {
        const { data } = await api.get("/favorites/ids");
        setFavIds(data);
      } catch (err) { console.error(err); }
    }
  };

  useEffect(() => {
    loadMeta();
  }, [user]);

  useEffect(() => {
    load();
  }, [q, genre, tag, year, is4k, type]);

  const toggleFav = async (id) => {
    if (!user || user === false) return toast.error("Connectez-vous");
    try {
      if (favIds.includes(id)) {
        await api.delete(`/favorites/${id}`);
        setFavIds(favIds.filter((x) => x !== id));
      } else {
        await api.post(`/favorites/${id}`);
        setFavIds([...favIds, id]);
      }
    } catch (err) { console.error(err); }
  };

  const years = Array.from({ length: 50 }, (_, i) => 2026 - i);

  return (
    <div className="pt-24 pb-24 max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
      <div className="space-y-2 mb-6">
        <div className="font-mono-mini text-[11px] uppercase tracking-[0.4em] text-[color:var(--ns-accent)]">
          Catalogue
        </div>
        <h1 className="font-display text-5xl uppercase">Parcourir</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          data-testid="type-tab-all"
          onClick={() => update("type", "")}
          className={`px-5 py-2 rounded-full text-sm font-mono-mini uppercase tracking-widest border transition ${
            !type ? "bg-white text-black border-white" : "bg-transparent border-white/15 text-zinc-300 hover:border-white/40"
          }`}
        >
          Tout
        </button>
        {CONTENT_TYPES.map((t) => (
          <button
            key={t.value}
            data-testid={`type-tab-${t.value}`}
            onClick={() => update("type", t.value)}
            className={`px-5 py-2 rounded-full text-sm font-mono-mini uppercase tracking-widest border transition ${
              type === t.value ? "bg-white text-black border-white" : "bg-transparent border-white/15 text-zinc-300 hover:border-white/40"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          data-testid="genre-chip-all"
          onClick={() => update("genre", "")}
          className={`px-4 py-1.5 rounded-full text-xs font-mono-mini uppercase tracking-widest border transition ${
            !genre ? "bg-[color:var(--ns-accent)] border-[color:var(--ns-accent)] text-white" : "border-white/15 text-zinc-400 hover:text-white hover:border-white/40"
          }`}
        >
          Toutes catégories
        </button>
        {GENRES.map((g) => (
          <button
            key={g}
            data-testid={`genre-chip-${g}`}
            onClick={() => update("genre", g === genre ? "" : g)}
            className={`px-4 py-1.5 rounded-full text-xs font-mono-mini uppercase tracking-widest border transition ${
              genre === g ? "bg-[color:var(--ns-accent)] border-[color:var(--ns-accent)] text-white" : "border-white/15 text-zinc-400 hover:text-white hover:border-white/40"
            }`}
          >
            {g}
          </button>
        ))}
      </div>

      {genre && GENRE_DESCRIPTIONS[genre] && (
        <div
          data-testid={`genre-description-${genre}`}
          className="mb-6 border-l-2 border-[color:var(--ns-accent)] bg-[color:var(--ns-card)] px-5 py-4 rounded-r-lg"
        >
          <div className="font-mono-mini text-[11px] uppercase tracking-[0.3em] text-[color:var(--ns-accent)] mb-2">
            {genre}
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed max-w-3xl">
            {GENRE_DESCRIPTIONS[genre]}
          </p>
        </div>
      )}

      {/* Secondary tag chips (ambiance / sub-genre) */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <span className="font-mono-mini text-[10px] uppercase tracking-[0.3em] text-zinc-500 mr-1">
          Ambiance
        </span>
        {TAGS.map((t) => (
          <button
            key={t}
            data-testid={`tag-chip-${t}`}
            onClick={() => update("tag", t === tag ? "" : t)}
            className={`px-3 py-1 rounded-full text-[11px] font-mono-mini uppercase tracking-widest border transition ${
              tag === t ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:text-white hover:border-white/40"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-8 items-center">
        <select
          data-testid="filter-year"
          value={year}
          onChange={(e) => update("year", e.target.value)}
          className="px-4 py-2 bg-[color:var(--ns-card)] border border-white/10 rounded-lg text-sm"
        >
          <option value="">Toutes années</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <button
          data-testid="filter-4k"
          onClick={() => update("is_4k", is4k === "true" ? "" : "true")}
          className={`px-4 py-2 rounded-lg text-sm font-mono-mini uppercase tracking-widest border transition ${
            is4k === "true" ? "bg-[color:var(--ns-accent)] border-[color:var(--ns-accent)]" : "bg-[color:var(--ns-card)] border-white/10"
          }`}
        >
          4K uniquement
        </button>
        {(q || genre || year || is4k || type || tag) && (
          <button
            onClick={() => setSearch({})}
            className="px-4 py-2 text-sm text-zinc-400 hover:text-white"
          >
            Réinitialiser
          </button>
        )}
        <div className="ml-auto font-mono-mini text-xs text-zinc-500 uppercase tracking-widest">
          {movies.length} résultats
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-400">Recherche…</div>
      ) : movies.length === 0 ? (
        <div className="text-zinc-500 text-center py-16">Aucun titre ne correspond à vos critères.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {movies.map((m) => (
            <MovieCard key={m.id} movie={m} inList={favIds.includes(m.id)} onToggleList={toggleFav} />
          ))}
        </div>
      )}
    </div>
  );
}
